import React, { useEffect, useState } from 'react';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { ExclamationOctagon } from 'react-bootstrap-icons';
import { withTranslation } from '../hoc';
import { formatRemaining } from './SessionCountdown';
import { getRemainingMs, isLoggedIn, markActivity } from './idleTimeout';

const TICK_MS = 1000;
export const WARNING_BAR_MS = 60 * 1000;

// Same colour as MsgInfo's warning type.
const WARNING_COLOR = '#ffa500';

// Mirrors the MsgInfo notification bar so it reads as a normal notification.
// One step above it, so the warning wins if both are showing.
const Container = styled.div`
  position: sticky;
  top: 68px;
  z-index: 501;
  width: 100%;
  background: var(--admincat-color-grey-4);
  box-shadow: 0 1px 1px 0 rgba(3, 3, 3, 0.05);

  @media (max-width: 800px) {
    top: 64px;
  }
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-height: 25px;
  margin-left: 80px;
  padding: 0.5em;
  color: ${WARNING_COLOR};
  font-size: 14px;

  @media (max-width: 768px) {
    margin-left: 16px;
  }

  svg {
    flex-shrink: 0;
  }
`;

const Message = styled.p`
  margin: 0;
  padding: 0;
`;

const Time = styled.strong`
  font-variant-numeric: tabular-nums;
`;

const Hint = styled.span`
  color: var(--admincat-color-grey-3);
`;

const StayButton = styled.button`
  height: 28px;
  padding: 0 14px;
  border: 0;
  border-radius: 6px;
  background: var(--admincat-color);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover,
  &:focus-visible {
    background: var(--admincat-dark-color);
  }
`;

// Shown under the header during the last minute before the inactivity
// logout. Any activity resets the timer and hides it, in every open tab.
export const SessionWarningBar = ({ tr = (key) => key }) => {
  const [remainingMs, setRemainingMs] = useState(getRemainingMs);

  useEffect(() => {
    const intervalId = setInterval(() => setRemainingMs(getRemainingMs()), TICK_MS);
    return () => clearInterval(intervalId);
  }, []);

  const isVisible = isLoggedIn() && remainingMs > 0 && remainingMs <= WARNING_BAR_MS;
  if (!isVisible) return null;

  const stayLoggedIn = () => {
    markActivity();
    setRemainingMs(getRemainingMs());
  };

  return (
    <Container role="status">
      <Bar>
        <ExclamationOctagon color={WARNING_COLOR} />
        <Message>
          {tr('You will be logged out due to inactivity in')}{' '}
          <Time>{formatRemaining(remainingMs)}</Time>.{' '}
          <Hint>{tr('Press any key or click anywhere to stay logged in.')}</Hint>
        </Message>
        <StayButton type="button" onClick={stayLoggedIn}>
          {tr('Stay logged in')}
        </StayButton>
      </Bar>
    </Container>
  );
};

export default compose(withTranslation)(SessionWarningBar);
